export const LOG_DISPLAY_TIMEZONE = "Europe/Warsaw";

const ISO_PREFIX = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)/;
const PM2_PREFIX = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}):/;

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? "00";
}

function warsawParts(date: Date, withMs: boolean): Intl.DateTimeFormatPart[] {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: LOG_DISPLAY_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    ...(withMs ? { fractionalSecondDigits: 3 as const } : {}),
  }).formatToParts(date);
}

export function formatLogTimestampPl(date: Date, withMs = true): string {
  const parts = warsawParts(date, withMs);
  const y = partValue(parts, "year");
  const mo = partValue(parts, "month");
  const d = partValue(parts, "day");
  const h = partValue(parts, "hour");
  const mi = partValue(parts, "minute");
  const s = partValue(parts, "second");
  const base = `${y}-${mo}-${d}T${h}:${mi}:${s}`;
  if (!withMs) return base;
  const frac = parts.find((p) => p.type === "fractionalSecond")?.value ?? "000";
  return `${base}.${frac}`;
}

export function extractLogTimestamp(text: string): Date | null {
  const iso = text.match(ISO_PREFIX);
  if (iso) {
    const d = new Date(iso[1]!);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const pm2 = text.match(PM2_PREFIX);
  if (pm2) {
    const d = new Date(`${pm2[1]}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatLogLineTextForDisplay(text: string): string {
  const iso = text.match(ISO_PREFIX);
  if (iso) {
    const d = new Date(iso[1]!);
    if (Number.isNaN(d.getTime())) return text;
    const withMs = /\.\d{3}Z$/.test(iso[1]!);
    return text.replace(ISO_PREFIX, formatLogTimestampPl(d, withMs));
  }
  const pm2 = text.match(PM2_PREFIX);
  if (pm2) {
    const d = new Date(`${pm2[1]}Z`);
    if (Number.isNaN(d.getTime())) return text;
    return text.replace(PM2_PREFIX, `${formatLogTimestampPl(d, false)}:`);
  }
  return text;
}

export function formatLogDayPl(date: Date): string {
  return date.toLocaleDateString("pl-PL", {
    timeZone: LOG_DISPLAY_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export type LogLevelHint = "debug" | "info" | "warn" | "error" | null;

export function detectLogLevel(text: string): LogLevelHint {
  const m = text.match(/\[(debug|info|warn|error)\]/i);
  if (!m) return null;
  return m[1]!.toLowerCase() as LogLevelHint;
}
